import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { Icon } from './Icon.js';
import JellyfishLoader from './JellyfishLoader';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import './AttachmentSelector.css';

const ProtocolReportSelector = ({ onSelect, onClose, selectedReport }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    // Deduplicate in-flight requests (e.g. React StrictMode double-mount in dev)
    const cacheKey = getCacheKey('ProtocolReportSelector_fetchReports', 'v1');
    return dedupeRequest(cacheKey, async () => {
      try {
        setLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user) {
          setReports([]);
          setLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('protocol_report_snapshots')
          .select(`
            id,
            protocol_id,
            title,
            snapshot_date,
            created_at,
            stats,
            biomarker_data,
            protocols:protocol_id (
              id,
              name,
              description,
              protocol_hacks (
                hack_id,
                position,
                biohacks (
                  id,
                  name,
                  image_url
                )
              )
            )
          `)
          .eq('user_id', user.id)
          .order('snapshot_date', { ascending: false });

        if (error) throw error;
        setReports(data || []);
      } catch (err) {
        console.error('Error fetching protocol reports:', err);
        setError('Failed to load protocol reports');
      } finally {
        setLoading(false);
      }
    });
  };

  const filteredReports = reports.filter(report =>
    report.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.protocols?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.protocols?.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleReportClick = (report) => {
    // If clicking on the already selected report, deselect it
    if (selectedReport && selectedReport.id === report.id) {
      onSelect(null);
    } else {
      onSelect(report);
    }
  };

  const handleDone = () => {
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="attachment-selector-overlay" onClick={onClose}>
        <div className="attachment-selector" onClick={(e) => e.stopPropagation()}>
          <div className="attachment-selector-loading">
            <JellyfishLoader />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="attachment-selector-overlay" onClick={onClose}>
      <div className="attachment-selector" onClick={(e) => e.stopPropagation()}>
        <div className="attachment-selector-header">
          <div className="attachment-selector-title">
            <Icon name="FileText" className="icon--sm" />
            <span>Select Protocol Report</span>
          </div>
          <div className="attachment-selector-actions">
            <button 
              className="attachment-search-toggle"
              onClick={() => setShowSearch(!showSearch)}
            >
              <Icon name="Search" className="icon--sm" />
            </button>
            <button className="attachment-selector-close" onClick={onClose}>
              <Icon name="X" className="icon--sm" />
            </button>
          </div>
        </div>

        {showSearch && (
          <div className="attachment-selector-search">
            <input
              type="text"
              placeholder="Search reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="attachment-search-input"
              autoFocus
            />
            <button 
              className="attachment-search-close"
              onClick={() => {
                setShowSearch(false);
                setSearchTerm('');
              }}
            >
              <Icon name="X" className="icon--xs" />
            </button>
          </div>
        )}

        <div className="attachment-selector-content">
          {error ? (
            <div className="attachment-selector-error">
              <Icon name="AlertCircle" className="icon--sm" />
              <span>{error}</span>
            </div>
          ) : filteredReports.length === 0 ? (
            <div className="attachment-selector-empty">
              <Icon name="FileText" className="icon--md" />
              <span>No protocol reports found</span>
              <span className="attachment-selector-empty-subtext">Create a protocol report in the Insights page first</span>
            </div>
          ) : (
            <div className="attachment-list">
              {filteredReports.map(report => {
                // Extract biohacks from protocol_hacks
                const biohacks = (report.protocols?.protocol_hacks || [])
                  .sort((a, b) => (a.position || 0) - (b.position || 0))
                  .map(ph => ph.biohacks)
                  .filter(Boolean)
                  .slice(0, 5); // Limit to 5 biohacks for display

                const reportTitle = report.title || report.protocols?.name || 'Protocol Report';
                const reportDate = formatDate(report.snapshot_date || report.created_at);
                const stats = report.stats || {};
                const adherencePct = stats.adherencePct || 0;

                return (
                  <div
                    key={report.id}
                    className={`attachment-item ${selectedReport?.id === report.id ? 'selected' : ''}`}
                    onClick={() => handleReportClick(report)}
                  >
                    <div className="attachment-item-content">
                      <div className="attachment-item-name">{reportTitle}</div>
                      <div className="attachment-item-description">
                        {report.protocols?.name && (
                          <span>{report.protocols.name}</span>
                        )}
                        {reportDate && (
                          <span className="attachment-item-meta"> • {reportDate}</span>
                        )}
                        {adherencePct > 0 && (
                          <span className="attachment-item-meta"> • {adherencePct.toFixed(0)}% adherence</span>
                        )}
                      </div>
                    </div>
                    <div className="attachment-item-right">
                      {biohacks.length > 0 && (
                        <div className="protocol-biohack-stack">
                          {biohacks.map((biohack, index) => (
                            <img
                              key={biohack.id || index}
                              src={biohack.image_url || '/default-biohack.jpg'}
                              alt={biohack.name}
                              className="protocol-biohack-disc"
                              style={{
                                zIndex: biohacks.length - index,
                                marginLeft: index > 0 ? '-8px' : '0'
                              }}
                              onError={(e) => {
                                e.target.src = '/default-biohack.jpg';
                              }}
                            />
                          ))}
                        </div>
                      )}
                      {selectedReport?.id === report.id && (
                        <div className="attachment-item-check">
                          <Icon name="Check" className="icon--xs" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="attachment-selector-footer">
          <div className="attachment-selection-info">
            {selectedReport ? `Selected: ${selectedReport.title || 'Protocol Report'}` : 'No report selected'}
          </div>
          <button
            className="attachment-done-btn"
            onClick={handleDone}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProtocolReportSelector;

